import { useState } from "react";
import { MessageCircle } from "lucide-react";
import { useWaLink } from "@/context/SiteDataContext";
import WhatsAppChooser, { useWhatsAppPresets } from "@/components/common/WhatsAppChooser";

/**
 * WhatsApp call-to-action.
 *
 * When preset questions are configured in the CMS, clicking opens a short
 * chooser first so the visitor picks what they are asking about. The chosen
 * line is pre-filled into the chat, so the team receives a message with real
 * context instead of a bare "Hi".
 *
 * A caller can pass an explicit `message` for a page-specific opener — the
 * product page does this to name the product. That still goes through the
 * chooser, with the specific message offered as the first option, so the
 * feature is never silently bypassed.
 */
export default function WhatsAppButton({
  message,
  label = "WhatsApp Us",
  className = "",
  variant = "solid",
  testId = "whatsapp-btn",
}) {
  const waLink = useWaLink();
  const presets = useWhatsAppPresets();
  const [open, setOpen] = useState(false);

  const base =
    "inline-flex items-center justify-center gap-2 rounded-md font-semibold transition-all duration-300 px-6 py-3";
  const styles = {
    solid: "bg-[#25D366] text-white hover:bg-[#1EBE5A]",
    outline: "border border-[#25D366] text-[#1EBE5A] hover:bg-[#25D366] hover:text-white",
    ghost: "text-[#1EBE5A] hover:text-[#25D366] px-0 py-0",
  };

  // With nothing configured there is nothing to choose from — go straight through.
  if (presets.length === 0) {
    return (
      <a
        href={waLink(message)}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        className={`${base} ${styles[variant]} ${className}`}
      >
        <MessageCircle className="w-5 h-5" strokeWidth={2} />
        {label}
      </a>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={testId}
        className={`${base} ${styles[variant]} ${className}`}
      >
        <MessageCircle className="w-5 h-5" strokeWidth={2} />
        {label}
      </button>
      <WhatsAppChooser open={open} onClose={() => setOpen(false)} contextMessage={message} />
    </>
  );
}
